import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { CreateMovimentacaoRequest } from '../../models/financas/create-movimentacao.request';
import { CreateMovimentacaoResponse } from '../../responses/financas/create-movimentacao.response';
import { GetMovimentacaoByIdResponse } from '../../responses/financas/get-movimentacao-by-id.response';
import { GetMovimentacaoResponse } from '../../responses/financas/get-movimentacao.response';
import { MovimentacaoLookupResponse } from '../../responses/financas/movimentacao-lookup.response';
import { PrintMovimentacaoResponse } from '../../responses/financas/print-movimentacao.response';
import { ReturnStruct } from '../../structs/return.struct';
import { PrintMovimentacaoRequest } from '../../requests/financas/print-movimentacao.request';
import { AcaoTagsEnum } from '../../../enums/financas/acao-tags.enum';

export interface GetMovimentacaoParams {
    pesquisa?: string;
    dataInicio?: string;
    dataFim?: string;
    idNaturezaMovimentacao?: number;
    idContaFinanceira?: number;
    idsCategoriaFinanceira?: number[];
    idsTagsMovimentacao?: number[];
    idsSituacaoMovimentacao?: number[];
    pagina?: number;
    tamanhoPagina?: number;
}

@Injectable({
    providedIn: 'root',
})
export class MovimentacaoService extends CommonService {
    private readonly apiUrl = `${environment.urlApiFinancas}Movimentacao`;

    constructor(private httpClient: HttpClient) {
        super();
    }

    public getLookup(idContaFinanceira?: number): Observable<MovimentacaoLookupResponse> {
        let url = `${this.apiUrl}/lookup`;
        if (idContaFinanceira) {
            url += `?idContaFinanceira=${idContaFinanceira}`;
        }
        return this.httpClient
            .get<MovimentacaoLookupResponse>(url, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public getAll(params: GetMovimentacaoParams): Observable<GetMovimentacaoResponse> {
        const query: string[] = [];
        if (params.pesquisa) query.push(`pesquisa=${encodeURIComponent(params.pesquisa)}`);
        if (params.dataInicio) query.push(`dataInicio=${encodeURIComponent(params.dataInicio)}`);
        if (params.dataFim) query.push(`dataFim=${encodeURIComponent(params.dataFim)}`);
        if (params.idNaturezaMovimentacao) query.push(`idNaturezaMovimentacao=${params.idNaturezaMovimentacao}`);
        if (params.idContaFinanceira) query.push(`idContaFinanceira=${params.idContaFinanceira}`);
        if (params.idsCategoriaFinanceira?.length) params.idsCategoriaFinanceira.forEach(id => query.push(`idsCategoriaFinanceira=${id}`));
        if (params.idsTagsMovimentacao?.length) params.idsTagsMovimentacao.forEach(id => query.push(`idsTagsMovimentacao=${id}`));
        if (params.idsSituacaoMovimentacao?.length) params.idsSituacaoMovimentacao.forEach(id => query.push(`idsSituacaoMovimentacao=${id}`));
        if (params.pagina) query.push(`pagina=${params.pagina}`);
        if (params.tamanhoPagina) query.push(`tamanhoPagina=${params.tamanhoPagina}`);
        const url = query.length ? `${this.apiUrl}?${query.join('&')}` : this.apiUrl;
        return this.httpClient
            .get<GetMovimentacaoResponse>(url, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public getById(id: number): Observable<GetMovimentacaoByIdResponse> {
        return this.httpClient
            .get<GetMovimentacaoByIdResponse>(`${this.apiUrl}/${id}`, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public create(request: CreateMovimentacaoRequest): Observable<CreateMovimentacaoResponse> {
        return this.httpClient
            .post<CreateMovimentacaoResponse>(this.apiUrl, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public update(id: number, request: CreateMovimentacaoRequest): Observable<CreateMovimentacaoResponse> {
        return this.httpClient
            .put<CreateMovimentacaoResponse>(`${this.apiUrl}/${id}`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public getPrint(request: PrintMovimentacaoRequest): Observable<PrintMovimentacaoResponse> {
        return this.httpClient
            .post<PrintMovimentacaoResponse>(`${this.apiUrl}/print`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public delete(id: number): Observable<ReturnStruct> {
        return this.httpClient
            .delete<ReturnStruct>(`${this.apiUrl}/${id}`, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public bulkDelete(ids: number[]): Observable<ReturnStruct> {
        return this.httpClient
            .post<ReturnStruct>(`${this.apiUrl}/bulk-delete`, { idsMovimentacao: ids }, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public bulkUpdateConta(idsMovimentacao: number[], idContaFinanceira: number): Observable<ReturnStruct> {
        return this.httpClient
            .post<ReturnStruct>(`${this.apiUrl}/bulk-update-conta`, { idsMovimentacao, idContaFinanceira }, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public bulkUpdateCategoria(idsMovimentacao: number[], idCategoriaFinanceira: number | null): Observable<ReturnStruct> {
        return this.httpClient
            .post<ReturnStruct>(`${this.apiUrl}/bulk-update-categoria`, { idsMovimentacao, idCategoriaFinanceira }, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public bulkUpdateTags(idsMovimentacao: number[], acao: AcaoTagsEnum, idsTagsMovimentacao: number[]): Observable<ReturnStruct> {
        return this.httpClient
            .post<ReturnStruct>(`${this.apiUrl}/bulk-update-tags`, { idsMovimentacao, acao, idsTagsMovimentacao }, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }
}
